import { useEffect, useState } from "react";
import {
  Bookmark,
  BookmarkCheck,
  Languages,
  Lightbulb,
  LoaderCircle,
  Quote,
  Sparkles,
  Volume2,
} from "lucide-react";
import { EMPTY_INSIGHT_MESSAGE } from "../constants";
import type { WordInsight } from "../types";

type InsightPanelProps = {
  insight: WordInsight | null;
  selectedWord: string;
  context: string;
  loading: boolean;
  error: string;
  saved: boolean;
  onSpeakWord: () => void;
  onSpeakTranslation: () => void;
  onSave: () => void;
  targetLanguage: string;
};

export function InsightPanel({
  insight,
  selectedWord,
  context,
  loading,
  error,
  saved,
  onSpeakWord,
  onSpeakTranslation,
  onSave,
  targetLanguage,
}: InsightPanelProps) {
  const [viewLanguage, setViewLanguage] = useState<"source" | "target">("source");

  useEffect(() => {
    setViewLanguage("source");
  }, [selectedWord]);

  if (!selectedWord) {
    return (
      <aside className="insight-panel insight-empty">
        <div className="empty-orbit"><Languages size={30} /></div>
        <h2>Kelimenin dünyasını aç</h2>
        <p>{EMPTY_INSIGHT_MESSAGE}</p>
        <div className="feature-pills">
          <span>Çeviri</span><span>Okunuş</span><span>Bağlam</span><span>Örnekler</span>
        </div>
      </aside>
    );
  }

  return (
    <aside className="insight-panel">
      <div className="insight-heading">
        <div>
          <p className="eyebrow">SEÇİLEN KELİME</p>
          <h2>{selectedWord}</h2>
        </div>
        <button className="round-action" onClick={onSpeakWord} aria-label="Kelimeyi sesli oku">
          <Volume2 size={21} />
        </button>
      </div>

      {insight && !loading && (
        <div className="language-view-toggle" aria-label="Bilgi dili">
          <button
            className={viewLanguage === "source" ? "is-active" : ""}
            onClick={() => setViewLanguage("source")}
            aria-pressed={viewLanguage === "source"}
          >
            {insight.detectedLanguage || "Orijinal dil"}
          </button>
          <button
            className={viewLanguage === "target" ? "is-active" : ""}
            onClick={() => setViewLanguage("target")}
            aria-pressed={viewLanguage === "target"}
          >
            {targetLanguage}
          </button>
        </div>
      )}

      {context && (
        <blockquote className="context-quote">
          <Quote size={16} />
          <span>
            {viewLanguage === "target" && insight?.contextTranslation
              ? insight.contextTranslation
              : context}
          </span>
        </blockquote>
      )}

      {loading && (
        <div className="analysis-loading">
          <LoaderCircle className="spin" size={26} />
          <div>
            <strong>Kelime sözlükte aranıyor</strong>
            <p>Anlam, çeviri ve örnekler getiriliyor…</p>
          </div>
        </div>
      )}

      {error && <p className="form-error insight-error" role="alert">{error}</p>}

      {insight && !loading && (
        <div className="insight-content">
          <div className="translation-hero">
            <div>
              <span>
                {viewLanguage === "source"
                  ? `${insight.detectedLanguage} sözlük görünümü`
                  : `${insight.detectedLanguage} → ${targetLanguage}`}
              </span>
              <strong>{viewLanguage === "source" ? selectedWord : insight.translation}</strong>
            </div>
            <button
              className="round-action pale"
              onClick={viewLanguage === "source" ? onSpeakWord : onSpeakTranslation}
              aria-label={viewLanguage === "source" ? "Kelimeyi sesli oku" : "Çeviriyi sesli oku"}
            >
              <Volume2 size={19} />
            </button>
          </div>

          <div className="word-meta">
            <span>{insight.partOfSpeech || "kelime"}</span>
            {insight.pronunciation && <code>{insight.pronunciation}</code>}
          </div>

          {insight.matchType === "inflected" && insight.matchedWord && (
            <p className="language-note">
              Bu çekimli biçim, <strong>{insight.matchedWord}</strong> sözlük maddesi üzerinden bulundu.
            </p>
          )}

          <section className="insight-section">
            <h3>
              <Lightbulb size={17} />
              {viewLanguage === "source" ? "Sözlük anlamı" : "Bu cümledeki anlamı"}
            </h3>
            <p>
              {viewLanguage === "source"
                ? insight.sourceDefinition || "Kaynak dilde sözlük anlamı bulunamadı."
                : insight.translatedDefinition ||
                  insight.contextMeaning ||
                  "Çevrilmiş sözlük anlamı bulunamadı."}
            </p>
            {viewLanguage === "target" &&
              insight.definition &&
              insight.definition !== insight.contextMeaning && (
              <p className="muted">{insight.definition}</p>
            )}
          </section>

          {viewLanguage === "source" &&
            (insight.synonyms.length > 0 || insight.antonyms.length > 0) && (
            <div className="relation-grid">
              <section>
                <h3>Yakın anlamlılar</h3>
                <div className="chip-list">
                  {insight.synonyms.map((word) => <span key={word}>{word}</span>)}
                  {insight.synonyms.length === 0 && <em>Bulunamadı</em>}
                </div>
              </section>
              <section>
                <h3>Karşıt anlamlılar</h3>
                <div className="chip-list antonyms">
                  {insight.antonyms.map((word) => <span key={word}>{word}</span>)}
                  {insight.antonyms.length === 0 && <em>Bulunamadı</em>}
                </div>
              </section>
            </div>
          )}

          {insight.examples.length > 0 && (
            <section className="insight-section examples">
              <h3><Sparkles size={17} /> Başka örnekler</h3>
              {insight.examples.map((example, index) => (
                <div className="example-pair" key={`${example.source}-${index}`}>
                  <strong>
                    {viewLanguage === "source" ? example.source : example.target}
                  </strong>
                </div>
              ))}
            </section>
          )}

          {insight.note && <p className="language-note">{insight.note}</p>}

          {insight.alternativeDefinitions && insight.alternativeDefinitions.length > 0 && (
            <section className="insight-section">
              <h3>Diğer anlamlar</h3>
              {insight.alternativeDefinitions.map((definition) => (
                <p className="muted" key={definition}>{definition}</p>
              ))}
            </section>
          )}

          <button className="save-button" onClick={onSave} disabled={saved}>
            {saved ? <BookmarkCheck size={19} /> : <Bookmark size={19} />}
            {saved ? "Sözlüğünde" : "Sözlüğe ekle"}
          </button>
          {insight.source && (
            <small className="model-note">
              Kaynak: <a href={insight.source.url} target="_blank" rel="noreferrer">
                {insight.source.name}
              </a> ({insight.source.license})
            </small>
          )}
        </div>
      )}
    </aside>
  );
}
